import { useCallback, useEffect } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getMessage } from '@gef-ui/localization/messages';
import ArrowRight from '@gef-ui/material-icons/ArrowRight';

import InfoCircleTooltip from 'components/atoms/GefMui/InfoCircleTooltip';
import { ComponentLoader } from 'components/index';
import fetch from 'features/common/fetch';
import { gtmDashboardSectionNames } from 'features/views/dashboard/constants';
import { useDetailButtonClickDataLayer } from 'features/views/dashboard/hooks';
import { fetchSemaphoreDashboard } from 'features/views/dashboard/semaphore/actions';
import { getSemaphoreData } from 'features/views/dashboard/semaphore/selectors';
import { transitionRowWithDefaultValues } from 'features/views/semaphore/constants';

import SemaphoreTableHead from './components/SemaphoreTableHead';
import SemaphoreTableRow from './components/SemaphoreTableRow';

export const Semaphore = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const pushToClickDetailButtonDataLayer = useDetailButtonClickDataLayer();

	const { firstMonth, secondMonth, thirdMonth } = useSelector(getSemaphoreData);

	useEffect(() => {
		dispatch(fetchSemaphoreDashboard());
	}, [dispatch]);

	const handleReload = useCallback(() => {
		dispatch(fetchSemaphoreDashboard());
	}, [dispatch]);

	const handleRedirect = useCallback(() => {
		pushToClickDetailButtonDataLayer({
			value: gtmDashboardSectionNames.SEMAPHORE,
		})();
		navigate('/semaphore');
	}, [navigate, pushToClickDetailButtonDataLayer]);

	const finalData = [
		{
			rowTitle: getMessage('dashboard:semaphore.to1Month'),
			data: firstMonth?.states || transitionRowWithDefaultValues,
			handleClickData: ['10', '40', '70'],
		},
		{
			rowTitle: getMessage('dashboard:semaphore.to2Months'),
			data: secondMonth?.states || transitionRowWithDefaultValues,
			handleClickData: ['20', '50', '80'],
		},
		{
			rowTitle: getMessage('dashboard:semaphore.to3Months'),
			data: thirdMonth?.states || transitionRowWithDefaultValues,
			handleClickData: ['30', '60', '90'],
		},
	];

	return (
		<Paper sx={{ p: 2, borderRadius: 1 }}>
			<Stack spacing={2}>
				<Stack
					direction="row"
					sx={{
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<Stack
						direction="row"
						sx={{
							alignItems: 'center',
						}}
					>
						<Typography sx={{ fontWeight: 'bold' }} variant="body1">
							{getMessage('dashboard:semaphore.title')}
						</Typography>
						<InfoCircleTooltip
							sx={{ fontSize: 15 }}
							text={getMessage('tooltips:semaphoreImpendingState')}
						/>
					</Stack>
					<Button
						sx={{ width: 'max-content', justifyContent: 'left' }}
						variant="text"
						endIcon={<ArrowRight />}
						onClick={handleRedirect}
					>
						{getMessage('dashboard:detail')}
					</Button>
				</Stack>
				<ComponentLoader
					fetchStorePath={fetch.constants.urls.semaphoreDashboard}
					forceInlineError
					onReload={handleReload}
				>
					<Paper
						sx={{
							px: 3,
							py: 2,
							backgroundColor: (theme) => theme.palette.background[100],
							borderRadius: 1,
						}}
					>
						<Table
							size="small"
							sx={{
								border: 'none !important',
								'& td': {
									pb: 0,
									pt: 0.5,
									px: 0.5,
								},
							}}
						>
							<TableBody>
								<SemaphoreTableHead />
								{finalData &&
									finalData.map((section) => (
										<SemaphoreTableRow {...section} key={section.rowTitle} />
									))}
							</TableBody>
						</Table>
					</Paper>
				</ComponentLoader>
			</Stack>
		</Paper>
	);
};

export default Semaphore;
